import { ApiError } from './api.js';

const userRoles = new Set(['MEMBER', 'RESEARCHER', 'SENIOR_RESEARCHER', 'SYSADMIN']);
const userStatuses = new Set(['ACTIVE', 'BLOCKED']);
const inviteRoles = new Set(['RESEARCHER', 'SENIOR_RESEARCHER']);
const messages = {
  MFA_REQUIRED: 'Введите код из приложения-аутентификатора или резервный код.',
  MFA_INVALID: 'Код неверен, истёк или уже использован. Дождитесь нового кода.',
  MFA_ENROLLMENT_REQUIRED: 'Подключите аутентификатор в разделе «Безопасность».',
  GALLERY_BUSY: 'Сервер обрабатывает другие изображения. Попробуйте позже.',
  INVALID_IMAGE_FORMAT: 'Допустимы только JPEG, PNG и WebP.',
  IMAGE_TOO_LARGE: 'Изображение превышает 15 МиБ.',
  PAYLOAD_TOO_LARGE: 'Превышен допустимый размер запроса.',
  INVALID_IMAGE: 'Не удалось прочитать изображение. Проверьте файл и ограничение 40 мегапикселей.',
  ANIMATED_IMAGE_NOT_ALLOWED: 'Загрузите статичное изображение без анимации.',
  INVALID_YANDEX_URL: 'Укажите публичную HTTPS-ссылку Яндекс Диска без дополнительных параметров.',
  INVALID_FILE_URL: 'Укажите внешнюю HTTPS-ссылку с доменным именем, без логина, IP-адреса и порта.',
  INVALID_SECTION: 'Раздел не найден. Обновите список разделов.', INACTIVE_SECTION: 'Для публикации нужен активный раздел.',
  INVALID_GALLERY_SECTION: 'Раздел галереи не найден. Обновите список.',
  INACTIVE_GALLERY_SECTION: 'Для публикации нужен активный раздел галереи.',
  SECTION_IN_USE: 'Раздел содержит фотографии или подразделы. Сначала перенесите их.',
  CATEGORY_IN_USE: 'Категория содержит статьи. Сначала перенесите или удалите их.',
  CAPTURE_DATE_REQUIRED: 'Укажите реальную дату съёмки перед публикацией.',
  INVALID_CAPTURE_DATE: 'Проверьте день, месяц и год съёмки.',
  INVALID_PARENT: 'Родительский раздел не найден. Обновите список.', SECTION_CYCLE: 'Раздел нельзя вложить в себя или свой подраздел.',
  MISSING_PLATFORM_LINK: 'Добавьте внешнюю ссылку для каждой выбранной платформы.',
  VARIANT_PLATFORM: 'Платформа ссылки должна быть выбрана у продукта.', INVALID_VARIANT: 'Версия ссылки не принадлежит этому продукту. Обновите запись.',
  INVALID_COVER: 'Обложка не найдена. Обновите список галереи.', UNPUBLISHED_COVER: 'Опубликуйте обложку в галерее или уберите её перед публикацией продукта.',
  INVALID_CREDENTIALS: 'Неверный логин или пароль.', UNAUTHENTICATED: 'Сессия завершена. Войдите снова.',
  FORBIDDEN: 'Недостаточно прав.', CSRF_INVALID: 'Не удалось проверить защиту запроса. Повторите попытку.',
  REGISTRATION_CLOSED: 'Регистрация временно закрыта.', MAINTENANCE: 'Техническое обслуживание. Попробуйте позже.',
  USER_CONFLICT: 'Имя пользователя или email уже заняты.', INVALID_INVITE: 'Инвайт недействителен или исчерпан.',
  VALIDATION_ERROR: 'Проверьте введённые поля.', RATE_LIMITED: 'Слишком много попыток. Попробуйте позже.',
  AUTH_BUSY: 'Сервер занят. Повторите попытку через несколько секунд.',
  INVALID_TOKEN: 'Ссылка недействительна, истекла или уже использована. Запросите новое письмо.',
  INVALID_PASSWORD: 'Неверный текущий пароль.', CONFLICT: 'Пароль уже изменён. Войдите заново или восстановите доступ.',
  VERSION_CONFLICT: 'Запись уже изменена. Ваш ввод сохранён; загрузите актуальную версию перед повторным редактированием.',
  LAST_ADMIN: 'Нельзя отключить, удалить или понизить последнего активного системного администратора.',
  INVALID_EXPIRY: 'Срок инвайта должен быть в будущем и не превышать один год.',
  SLUG_CONFLICT: 'Такой адрес уже занят.',
  CATEGORY_INACTIVE: 'Категория отключена. Выберите активную категорию для публикации.',
  INVALID_CATEGORY: 'Категория не найдена. Обновите список категорий.',
  INVALID_LINK: 'Укажите ссылку http, https или mailto без пробелов и учётных данных.',
  INVALID_CARDS: 'Каждый раздел можно добавить в карточки только один раз.',
  IDEMPOTENCY_CONFLICT: 'Параметры повторной отправки изменились. Проверьте поля и отправьте сообщение ещё раз.',
};
export function createAuthService(transport = (...args) => fetch(...args), notify = () => window.dispatchEvent(new Event('fixlan:auth_change'))) {
  let user = null, status = 'loading', error = '', revision = 0, busy = false;
  function set(next, state, message = '') { user = next; status = state; error = message; notify(); }
  async function request(path, options = {}) {
    let response;
    try { response = await transport(`/api/v1${path}`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000), ...options }); }
    catch { throw new ApiError('Нет ответа от сервера. Проверьте соединение; результат операции можно уточнить обновлением сессии.'); }
    if (response.status === 204) return null;
    let payload;
    try { payload = await response.json(); } catch { throw new ApiError('Некорректный ответ сервера.', response.status); }
    if (!response.ok) {
      const code = payload?.error?.code;
      throw new ApiError(messages[code] || (response.status === 400 ? 'Проверьте введённые поля.' : 'Операция временно недоступна.'), response.status, code);
    }
    return payload.data;
  }
  async function mutate(path, body, method = 'POST', multipart = false, extraHeaders = {}) {
    // Tokens are tied to the current cookie context; obtain a fresh one for each mutation.
    for (let attempt = 0; attempt < 2; attempt++) {
      const { csrfToken } = await request('/auth/csrf');
      try { return await request(path, { method,
        headers: { ...(!multipart ? { 'Content-Type': 'application/json' } : {}), 'X-CSRF-Token': csrfToken, ...extraHeaders },
        body: multipart ? body : JSON.stringify(body ?? {}),
        ...(multipart ? { signal: AbortSignal.timeout(60000) } : {}),
      }); }
      catch (failure) {
        if (multipart && (failure.code === 'UNAVAILABLE' || failure.status >= 500)) {
          throw new ApiError('Результат загрузки неизвестен. Проверьте список галереи перед повторной отправкой.', failure.status, failure.code);
        }
        if (failure.code !== 'CSRF_INVALID' || failure.status !== 403 || attempt === 1) throw failure;
      }
    }
  }
  async function change(path, body) {
    if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
    busy = true; revision++;
    try {
      const result = await mutate(path, body);
      set(result?.user ?? null, result?.user ? 'authenticated' : 'guest');
      return result?.user;
    } catch (failure) {
      if (path === '/auth/logout' && failure.status === 401) { set(null, 'guest'); return; }
      throw failure;
    } finally { busy = false; }
  }
  async function remove(path, id, version) {
    if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
    if (!id || !Number.isInteger(version) || version < 1) throw new ApiError('Обновите список перед удалением.', 400, 'VALIDATION_ERROR');
    busy = true;
    try { return await mutate(`${path}/${encodeURIComponent(id)}`, { version }, 'DELETE'); }
    finally { busy = false; }
  }
  return {
    getCurrentUser: () => user,
    getState: () => ({ status, error }),
    async refresh() {
      if (busy) return;
      const current = ++revision;
      try { const next = await request('/auth/me'); if (current === revision) set(next, 'authenticated'); }
      catch (failure) { if (current === revision) set(null, failure.status === 401 ? 'guest' : 'error', failure.status === 401 ? '' : failure.message); }
    },
    login: (identifier, password, rememberMe, factor) => change('/auth/login', { identifier: identifier.trim(), password, rememberMe, ...(factor?.trim() ? { factor: factor.trim() } : {}) }),
    setupMfa: body => mutate('/auth/mfa/setup', body),
    async confirmMfa(factor) {
      const result = await mutate('/auth/mfa/confirm', { factor });
      set(null, 'guest');
      return result;
    },
    recoverMfa: body => mutate('/auth/mfa/recover', body),
    register: ({ username, email, password, inviteCode }) => change('/auth/register', { username: username.trim(), email: email.trim(), password, ...(inviteCode?.trim() ? { inviteCode: inviteCode.trim() } : {}) }),
    logout: () => change('/auth/logout'),
    async saveCategory(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate(id ? `/admin/categories/${encodeURIComponent(id)}` : '/admin/categories', data, id ? 'PATCH' : 'POST'); }
      finally { busy = false; }
    },
    deleteCategory: (id, version) => remove('/admin/categories', id, version),
    async savePost(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate(id ? `/admin/posts/${encodeURIComponent(id)}` : '/admin/posts', data, id ? 'PATCH' : 'POST'); }
      finally { busy = false; }
    },
    deletePost: (id, version) => remove('/admin/posts', id, version),
    async saveFile(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate(id ? `/admin/files/${encodeURIComponent(id)}` : '/admin/files', data, id ? 'PATCH' : 'POST'); }
      finally { busy = false; }
    },
    deleteFile: (id, version) => remove('/admin/files', id, version),
    async saveFileSection(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate(id ? `/admin/file-sections/${encodeURIComponent(id)}` : '/admin/file-sections', data, id ? 'PATCH' : 'POST'); }
      finally { busy = false; }
    },
    deleteFileSection: (id, version) => remove('/admin/file-sections', id, version),
    async saveGallerySection(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate(id ? `/admin/gallery-sections/${encodeURIComponent(id)}` : '/admin/gallery-sections', data, id ? 'PATCH' : 'POST'); }
      finally { busy = false; }
    },
    async deleteGallerySection(id, version) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указан раздел галереи.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/gallery-sections/${encodeURIComponent(id)}`, { version }, 'DELETE');
      } finally { busy = false; }
    },
    async saveFileProduct(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate(id ? `/admin/file-products/${encodeURIComponent(id)}` : '/admin/file-products', data, id ? 'PATCH' : 'POST'); }
      finally { busy = false; }
    },
    async uploadGallery(metadata, image) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!(image instanceof Blob) || image.size === 0) throw new ApiError('Выберите изображение.', 400, 'VALIDATION_ERROR');
        if (image.size > 15 * 1024 * 1024) throw new ApiError(messages.IMAGE_TOO_LARGE, 413, 'IMAGE_TOO_LARGE');
        const body = new FormData();
        body.append('metadata', JSON.stringify(metadata));
        body.append('image', image, image.name || 'image');
        return await mutate('/admin/gallery', body, 'POST', true);
      } finally { busy = false; }
    },
    async deleteGallery(id, version) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указана запись галереи.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/gallery/${encodeURIComponent(id)}`, { version }, 'DELETE');
      } finally { busy = false; }
    },
    async saveGallery(data, id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указана запись галереи.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/gallery/${encodeURIComponent(id)}`, data, 'PATCH');
      } finally { busy = false; }
    },
    async saveFeedback(id, version, nextStatus) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указано обращение.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/feedback/${encodeURIComponent(id)}`, { version, status: nextStatus }, 'PATCH');
      } finally { busy = false; }
    },
    async deleteFeedback(id, version) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указано обращение.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/feedback/${encodeURIComponent(id)}`, { version }, 'DELETE');
      } finally { busy = false; }
    },
    async saveUser(id, change) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указан пользователь.', 400, 'VALIDATION_ERROR');
        const body = {
          ...(userRoles.has(change?.role) ? { role: change.role } : {}),
          ...(userStatuses.has(change?.status) ? { status: change.status } : {}),
        };
        if (!Object.keys(body).length) throw new ApiError('Выберите допустимое изменение доступа.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/users/${encodeURIComponent(id)}`, body, 'PATCH');
      } finally { busy = false; }
    },
    async revokeUserSessions(id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указан пользователь.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/users/${encodeURIComponent(id)}/revoke-sessions`);
      } finally { busy = false; }
    },
    async deleteUser(id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указан пользователь.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/users/${encodeURIComponent(id)}`, undefined, 'DELETE');
      } finally { busy = false; }
    },
    async createInvite(input) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        const expiresAt = new Date(input?.expiresAt), maxUses = input?.maxUses;
        const expiryTime = expiresAt.getTime(), now = Date.now();
        if (!inviteRoles.has(input?.role) || !Number.isInteger(maxUses) || maxUses < 1 || maxUses > 1000
          || !Number.isFinite(expiryTime) || expiryTime <= now || expiryTime > now + 365 * 86400000) {
          throw new ApiError('Проверьте параметры инвайта.', 400, 'VALIDATION_ERROR');
        }
        return await mutate('/admin/invites', { role: input.role, expiresAt: expiresAt.toISOString(), maxUses });
      } finally { busy = false; }
    },
    async revokeInvite(id) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!id) throw new ApiError('Не указан инвайт.', 400, 'VALIDATION_ERROR');
        return await mutate(`/admin/invites/${encodeURIComponent(id)}/revoke`);
      } finally { busy = false; }
    },
    async submitFeedback(data, key) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try {
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(key ?? '')) throw new ApiError('Не удалось подготовить отправку. Обновите страницу.', 400, 'VALIDATION_ERROR');
        return await mutate('/feedback', data, 'POST', false, { 'Idempotency-Key': key });
      } catch (failure) {
        if (!failure.status || (failure.status >= 500 && failure.code !== 'MAINTENANCE') || failure.code === 'UNAVAILABLE') {
          throw new ApiError('Результат отправки неизвестен. Не меняя поля, повторите отправку: тот же ключ защитит от дубликата.', failure.status, failure.code);
        }
        throw failure;
      } finally { busy = false; }
    },
    async saveProfile(data) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      try { return await mutate('/admin/settings', data, 'PATCH'); }
      finally { busy = false; }
    },
    async saveSiteControls(input) {
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      const data = {
        ...(Number.isInteger(input?.version) && input.version > 0 ? { version: input.version } : {}),
        ...(typeof input?.registrationEnabled === 'boolean' ? { registrationEnabled: input.registrationEnabled } : {}),
        ...(typeof input?.maintenanceMode === 'boolean' ? { maintenanceMode: input.maintenanceMode } : {}),
        ...(typeof input?.maintenanceMessage === 'string' ? { maintenanceMessage: input.maintenanceMessage } : {}),
      };
      if (!data.version || Object.keys(data).length < 2 || (data.maintenanceMessage !== undefined
        && (!data.maintenanceMessage.trim() || data.maintenanceMessage.length > 500))) {
        throw new ApiError('Проверьте параметры режимов сайта.', 400, 'VALIDATION_ERROR');
      }
      busy = true;
      try { return await mutate('/admin/site-controls', data, 'PATCH'); }
      finally { busy = false; }
    },
    async accountAction(action, body) {
      if (!['forgot-password', 'resend-verification', 'verify-email', 'reset-password', 'change-password'].includes(action)) throw new Error('Unknown account action');
      if (busy) throw new ApiError('Дождитесь завершения текущей операции.');
      busy = true;
      if (['reset-password', 'change-password'].includes(action)) revision++;
      try {
        await mutate(`/auth/${action}`, body);
        if (['reset-password', 'change-password'].includes(action)) set(null, 'guest');
        if (action === 'verify-email' && user) set({ ...user }, status);
      } catch (failure) {
        if (action === 'change-password' && failure.status === 401 && !['MFA_REQUIRED', 'MFA_INVALID'].includes(failure.code)) set(null, 'guest');
        throw failure;
      } finally { busy = false; }
    },
    async checkAdmin() { return request('/admin/session'); },
  };
}
export const authService = createAuthService();
